// Result management service
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  writeBatch,
  serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { v4 as uuidv4 } from 'uuid';

// Create a result for a player in a game
export const createResult = async (gameId, playerId, competitionId, categoryId, pointsEntered, participated) => {
  const resultId = uuidv4();
  const resultRef = doc(db, 'results', resultId);

  const result = {
    gameId,
    playerId,
    competitionId,
    categoryId,
    pointsEntered: participated ? pointsEntered : null,
    participated,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp()
  };

  await setDoc(resultRef, result);

  return {
    id: resultId,
    ...result
  };
};

// Get result by ID
export const getResult = async (resultId) => {
  const resultRef = doc(db, 'results', resultId);
  const resultSnap = await getDoc(resultRef);

  if (!resultSnap.exists()) {
    return null;
  }

  return {
    id: resultSnap.id,
    ...resultSnap.data()
  };
};

// Get all results for a game
export const getResultsForGame = async (gameId) => {
  const resultsRef = collection(db, 'results');
  const q = query(resultsRef, where('gameId', '==', gameId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Get all results for a competition
export const getResultsForCompetition = async (competitionId) => {
  const resultsRef = collection(db, 'results');
  const q = query(resultsRef, where('competitionId', '==', competitionId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Get all results for a player
export const getResultsForPlayer = async (playerId) => {
  const resultsRef = collection(db, 'results');
  const q = query(resultsRef, where('playerId', '==', playerId));
  const querySnapshot = await getDocs(q);

  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

// Update a result
export const updateResult = async (resultId, pointsEntered, participated) => {
  const resultRef = doc(db, 'results', resultId);
  await updateDoc(resultRef, {
    pointsEntered: participated ? pointsEntered : null,
    participated,
    updatedAt: serverTimestamp()
  });
};

// Batch save results for a game (used when entering all results at once)
export const saveGameResults = async (gameId, resultsData) => {
  const batch = writeBatch(db);

  // First, get existing results for this game
  const existingResults = await getResultsForGame(gameId);
  const existingResultsMap = new Map(
    existingResults.map(r => [r.playerId, r])
  );

  resultsData.forEach(resultData => {
    const existing = existingResultsMap.get(resultData.playerId);

    if (existing) {
      // Update existing result
      const resultRef = doc(db, 'results', existing.id);
      batch.update(resultRef, {
        pointsEntered: resultData.participated ? resultData.pointsEntered : null,
        participated: resultData.participated,
        eliminationPosition: resultData.eliminationPosition || null,
        updatedAt: serverTimestamp()
      });
    } else {
      // Create new result
      const resultId = uuidv4();
      const resultRef = doc(db, 'results', resultId);
      batch.set(resultRef, {
        gameId,
        playerId: resultData.playerId,
        competitionId: resultData.competitionId,
        categoryId: resultData.categoryId,
        pointsEntered: resultData.participated ? resultData.pointsEntered : null,
        participated: resultData.participated,
        eliminationPosition: resultData.eliminationPosition || null,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
  });

  await batch.commit();
};

// Delete a result
export const deleteResult = async (resultId) => {
  const resultRef = doc(db, 'results', resultId);
  await deleteDoc(resultRef);
};

// Delete all results for a game
export const deleteAllResultsForGame = async (gameId) => {
  const resultsToDelete = await getResultsForGame(gameId);
  const batch = writeBatch(db);

  resultsToDelete.forEach(result => {
    const resultRef = doc(db, 'results', result.id);
    batch.delete(resultRef);
  });

  await batch.commit();
};

// Validate that all players have results for a game
export const validateGameResults = (players, results) => {
  const errors = [];

  // Check that every player has a result
  const playerIdsWithResults = new Set(results.map(r => r.playerId));
  players.forEach(player => {
    if (!playerIdsWithResults.has(player.id)) {
      errors.push(`Missing result for player: ${player.displayName}`);
    }
  });

  // Check that participated players have valid points
  results.forEach(result => {
    if (result.participated) {
      if (result.pointsEntered === null || result.pointsEntered === undefined) {
        const player = players.find(p => p.id === result.playerId);
        errors.push(`Missing points for player: ${player?.displayName || 'Unknown'}`);
      }
    }
  });

  // Check that at least one player participated
  const participantCount = results.filter(r => r.participated).length;
  if (participantCount === 0) {
    errors.push('At least one player must have participated in the game');
  }

  return errors;
};

// Update elimination order for knockout mode games
export const updateEliminationOrder = async (gameId, eliminationOrder) => {
  const batch = writeBatch(db);

  // Get existing results for this game
  const existingResults = await getResultsForGame(gameId);

  // Update each result with its elimination position
  existingResults.forEach(result => {
    const eliminationIndex = eliminationOrder.indexOf(result.playerId);
    const eliminationPosition = eliminationIndex >= 0 ? eliminationIndex + 1 : null;

    const resultRef = doc(db, 'results', result.id);
    batch.update(resultRef, {
      eliminationPosition,
      updatedAt: serverTimestamp()
    });
  });

  await batch.commit();
};
